/**
 * category-review.component.ts
 * ----------------------------
 * Module 7 + 8 — AI-Based Guardrail Category Suggestion & Category Selection.
 *
 * State machine:
 *  'loading'  → 3-phase animated loader (Module 7: extraction + LLM analysis).
 *  'ready'    → Module 8 dual-panel Category Selection UI.
 *  'error'    → Error state with retry button.
 *
 * Loader phases:
 *  Phase 1 — "Documents Uploaded"      (always complete when we reach this page)
 *  Phase 2 — "Content Extraction"      (extraction progress 0→100%)
 *  Phase 3 — "AI Guardrail Analysis"   (LLM progress 0→100%)
 *
 * Ready state (Module 8 dual-panel):
 *  Left panel  — Available Categories (all app-type-filtered categories NOT yet selected).
 *  Right panel — Selected Categories for Analysis (AI-suggested auto-added + manually added).
 *  AI categories show a confidence dropdown with justification & evidence on click.
 */

import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { Router }       from '@angular/router';

import {
  CategoryService,
  SuggestedCategory,
  SuggestionsResponse,
} from '../../core/services/category.service';
import { ExtractionService } from '../../core/services/extraction.service';
import { SessionService }    from '../../core/services/session.service';
import { AssessmentService } from '../../core/services/assessment.service';

/** How often (ms) to poll status endpoints. */
const POLL_INTERVAL_MS = 2000;

type PageState   = 'loading' | 'ready' | 'error';
type LoaderPhase = 'extraction' | 'analysis';

// ---------------------------------------------------------------------------
// Selected-entry discriminated union
// ---------------------------------------------------------------------------

/** A category that the LLM suggested (has full justification / evidence data). */
export interface AiSelectedEntry {
  type:         'ai';
  name:         string;
  aiData:       SuggestedCategory;
  dropdownOpen: boolean;
}

/** A category manually added by the user from the Available panel. */
export interface ManualEntry {
  type:         'manual';
  name:         string;
  dropdownOpen: false;
}

export type SelectedEntry = AiSelectedEntry | ManualEntry;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector:    'app-category-review',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './category-review.component.html',
  styleUrl:    './category-review.component.scss',
})
export class CategoryReviewComponent implements OnInit, OnDestroy {

  // ── Page state ────────────────────────────────────────────────────────────
  pageState    = signal<PageState>('loading');
  errorMessage = signal<string>('');

  // ── Module 7 — loader signals ─────────────────────────────────────────────
  currentPhase       = signal<LoaderPhase>('extraction');
  extractionProgress = signal<number>(0);
  extractionMessage  = signal<string>('🚀 Preparing to extract content from your documents…');
  extractionDetail   = signal<string>('');
  extractionDone     = signal<boolean>(false);
  currentFile        = signal<string>('');   // current file being processed
  analysisProgress   = signal<number>(0);
  analysisMessage    = signal<string>('Waiting for extraction to complete…');
  analysisDone       = signal<boolean>(false);

  metadata = signal<SuggestionsResponse['metadata'] | null>(null);

  /** Overall progress bar: extraction = 0–50 %, analysis = 50–100 %. */
  overallProgress = computed(() => {
    if (this.currentPhase() === 'extraction') {
      return Math.round(this.extractionProgress() * 0.5);
    }
    return 50 + Math.round(this.analysisProgress() * 0.5);
  });

  // ── Module 8 — dual-panel state ───────────────────────────────────────────

  /**
   * All available category names for this session (app-type-filtered).
   * Drives the LEFT panel.
   */
  allCategoryNames = signal<string[]>([]);

  /**
   * Persistent map of AI suggestion data keyed by category name.
   * Populated once on analysis completion and never cleared, so AI data
   * is still accessible even after a category is moved back to the
   * Available panel.
   */
  aiDataMap = signal<Map<string, SuggestedCategory>>(new Map());

  /**
   * Currently selected categories (right panel).
   * AI-suggested entries are prepended automatically; users then add/remove.
   */
  selectedList = signal<SelectedEntry[]>([]);

  /**
   * Left-panel list: all categories NOT already in the selected list.
   * Computed so it stays reactive to add/remove actions.
   */
  availableList = computed<string[]>(() => {
    const selectedNames = new Set(this.selectedList().map(e => e.name));
    return this.allCategoryNames().filter(n => !selectedNames.has(n));
  });

  /** Total count of selected categories (for button enable state). */
  selectedCount = computed(() => this.selectedList().length);

  /** Left-panel search query (case-insensitive filter). */
  leftSearchQuery = '';

  /** Filtered left panel list based on search query. */
  filteredAvailableList = computed<string[]>(() => {
    const q = this.leftSearchQuery.trim().toLowerCase();
    if (!q) { return this.availableList(); }
    return this.availableList().filter(n => n.toLowerCase().includes(q));
  });

  /**
   * Tracks which available-panel items have their AI detail popover open.
   * Key = category name, value = true when open.
   */
  availAiOpenMap = signal<Record<string, boolean>>({});

  // ── Private ───────────────────────────────────────────────────────────────
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _sessionId: string | null = null;

  constructor(
    private readonly categorySvc:   CategoryService,
    private readonly extractionSvc: ExtractionService,
    private readonly sessionSvc:    SessionService,
    private readonly assessmentSvc: AssessmentService,
    private readonly router:        Router,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this._sessionId = this.sessionSvc.sessionId();
    if (!this._sessionId) {
      this.router.navigate(['/']);
      return;
    }
    this._startAnalysis();
  }

  ngOnDestroy(): void {
    this._stopPolling();
  }

  // ── Left-panel dropdown close on outside click ───────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Close right-panel AI dropdowns
    if (!target.closest('.sel-card__ai-badge')) {
      this.selectedList.update(list =>
        list.map((e): SelectedEntry => ({ ...e, dropdownOpen: false } as SelectedEntry))
      );
    }
    // Close left-panel AI popovers
    if (!target.closest('.avail-item__ai-badge')) {
      this.availAiOpenMap.set({});
    }
  }

  // ── Public: Module 8 panel actions ───────────────────────────────────────

  /**
   * Add a category from the left (Available) panel to the right (Selected) panel.
   * If the category was originally AI-suggested, it is restored as an AiSelectedEntry
   * (with all confidence / justification / evidence data intact).
   * Otherwise a plain ManualEntry is created.
   */
  addCategory(name: string): void {
    const aiData = this.aiDataMap().get(name);
    const entry: SelectedEntry = aiData
      ? { type: 'ai', name, aiData, dropdownOpen: false }
      : { type: 'manual', name, dropdownOpen: false };
    this.selectedList.update(list => [...list, entry]);
    // Close any open available-panel AI popover for this item
    this.availAiOpenMap.update(m => ({ ...m, [name]: false }));
  }

  /**
   * Remove a category from the right (Selected) panel.
   * Both AI-suggested and manually-added entries can be removed.
   * If the category name is not already in allCategoryNames (e.g. it was an
   * AI-only suggestion that was never in the available list), add it back so
   * it appears in the Available Categories panel.
   */
  removeCategory(name: string): void {
    this.selectedList.update(list => list.filter(e => e.name !== name));
    // Ensure the removed category appears in the Available panel
    if (!this.allCategoryNames().includes(name)) {
      this.allCategoryNames.update(names => [...names, name].sort((a, b) => a.localeCompare(b)));
    }
  }

  /**
   * Toggle the AI detail popover for a category in the Available (left) panel.
   * Only one popover is open at a time.
   */
  toggleAvailAiPopover(event: Event, name: string): void {
    event.stopPropagation();
    const current = this.availAiOpenMap()[name] ?? false;
    // Close all, then toggle the clicked one
    this.availAiOpenMap.set({ [name]: !current });
  }

  /** Check if a category in the available panel has AI data. */
  hasAiData(name: string): boolean {
    return this.aiDataMap().has(name);
  }

  /** Get AI data for a category (used in available panel popover). */
  getAiData(name: string): SuggestedCategory | undefined {
    return this.aiDataMap().get(name);
  }

  /** Check if the available-panel AI popover is open for a category. */
  isAvailAiOpen(name: string): boolean {
    return this.availAiOpenMap()[name] ?? false;
  }

  /**
   * Toggle the AI-detail dropdown for a selected AI category.
   * Only one dropdown is open at a time.
   */
  toggleAiDropdown(event: Event, index: number): void {
    event.stopPropagation();
    this.selectedList.update(list =>
      list.map((entry, i): SelectedEntry => {
        if (i === index && entry.type === 'ai') {
          return { ...entry, dropdownOpen: !entry.dropdownOpen };
        }
        return { ...entry, dropdownOpen: false } as SelectedEntry;
      })
    );
  }

  /** Navigate back to the Document Upload page. */
  goBack(): void {
    this.router.navigate(['/document-upload']);
  }

  /**
   * Finalise the category selection, fire the assessment pipeline in the
   * background, then navigate to the Assessment Report page.
   *
   * The assessment pipeline is triggered here (fire-and-forget) so that it
   * starts immediately while the user is being routed.  The Assessment Report
   * page detects the in-progress pipeline and enters its loading state directly.
   */
  proceed(): void {
    if (this.selectedCount() === 0) { return; }
    const names = this.selectedList().map(e => e.name);
    this.sessionSvc.setFinalizedCategories(names);
    this.sessionSvc.markStepComplete('category-review');

    // Fire the assessment pipeline immediately — navigate regardless of outcome.
    // The assessment-report page will poll for status once it loads.
    const sid = this._sessionId;
    if (sid) {
      // Reset any prior run first, then start fresh.
      this.assessmentSvc.reset(sid).subscribe({
        next:  () => this.assessmentSvc.startAssessment(sid, names).subscribe(),
        error: () => this.assessmentSvc.startAssessment(sid, names).subscribe(),
      });
    }

    this.router.navigate(['/assessment-report']);
  }

  // ── Public: retry ─────────────────────────────────────────────────────────

  retryAnalysis(): void {
    if (!this._sessionId) { return; }
    this.pageState.set('loading');
    this.currentPhase.set('extraction');
    this.extractionProgress.set(0);
    this.extractionDone.set(false);
    this.currentFile.set('');
    this.analysisProgress.set(0);
    this.analysisDone.set(false);
    this.errorMessage.set('');
    this.extractionMessage.set('Resetting analysis…');
    this.analysisMessage.set('Waiting for extraction to complete…');
    this.selectedList.set([]);
    this.allCategoryNames.set([]);
    this.aiDataMap.set(new Map());
    this.availAiOpenMap.set({});

    this.categorySvc.resetSuggestion(this._sessionId).subscribe({
      next:  () => this._startAnalysis(),
      error: () => this._startAnalysis(),
    });
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  confidenceClass(score: number): string {
    if (score >= 80) { return 'conf-high'; }
    if (score >= 50) { return 'conf-medium'; }
    return 'conf-low';
  }

  confidenceLabel(score: number): string {
    if (score >= 80) { return 'High'; }
    if (score >= 50) { return 'Medium'; }
    return 'Low';
  }

  /** Split referenced_text into bullet-point sentences (split on ". "). */
  splitEvidence(text: string): string[] {
    if (!text) { return []; }
    return text
      .split(/\.\s+/)
      .map(s => s.trim())
      .filter(s => s.length > 10);
  }

  /**
   * Truncate a file name for display in the loader.
   * Keeps the first 3 "words" (split on spaces, underscores, hyphens) then
   * appends "…" and the original file extension.
   * e.g. "My_Application_Security_Design_Plan_v2.docx" → "My Application Security….docx"
   */
  truncateFileName(raw: string, wordLimit = 3): string {
    if (!raw) { return ''; }
    // Strip any leading path segments
    const name = raw.split(/[\\/]/).pop() ?? raw;
    const dotIdx = name.lastIndexOf('.');
    const stem = dotIdx > 0 ? name.slice(0, dotIdx) : name;
    const ext  = dotIdx > 0 ? name.slice(dotIdx)    : '';
    // Split stem into words
    const words = stem.split(/[\s_\-]+/).filter(w => w.length > 0);
    if (words.length <= wordLimit) { return name; }
    return words.slice(0, wordLimit).join(' ') + '…' + ext;
  }

  /**
   * Scan a backend message string and truncate any embedded file names
   * (tokens ending with a recognised document extension).
   * e.g. "Extracting My_Long_Document_Name_v2.pdf…" →
   *      "Extracting My Long Document….pdf…"
   */
  formatMessage(msg: string): string {
    if (!msg) { return ''; }
    return msg.replace(
      /\S+\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|png|jpe?g|tiff?|gif|eml|msg)/gi,
      (match) => this.truncateFileName(match),
    );
  }

  /** Type guard — check if entry is an AI entry. */
  isAi(entry: SelectedEntry): entry is AiSelectedEntry {
    return entry.type === 'ai';
  }

  trackByName(_i: number, name: string): string { return name; }
  trackByEntryName(_i: number, entry: SelectedEntry): string { return entry.name; }

  // ── Private: analysis pipeline (Module 7) ─────────────────────────────────

  private _startAnalysis(): void {
    if (!this._sessionId) { return; }
    this.extractionSvc.getExtractionStatus(this._sessionId).subscribe({
      next: (s) => {
        if (s.extraction_done) {
          // Already complete (e.g. back-navigation with no new files)
          this.extractionProgress.set(100);
          this.extractionMessage.set('✅ Content extraction complete!');
          const processedCount = s.completed_files > 0
            ? s.completed_files
            : s.extracted_files.length;
          this.extractionDetail.set(`${processedCount} file(s) processed`);
          this.extractionDone.set(true);
          this.currentPhase.set('analysis');
          this._startCategoryAnalysis();
        } else if (s.status === 'running') {
          // Incremental extraction already in progress (new files were just
          // added via the upload page).  Don't re-trigger — just attach to
          // the running task and show live progress.
          this.currentPhase.set('extraction');
          this.extractionProgress.set(s.progress);
          this.extractionMessage.set(s.message || '⏳ Extracting new file(s)…');
          if (s.total_files > 0) {
            this.extractionDetail.set(
              `${s.completed_files} of ${s.total_files} file(s) processed`
            );
          }
          this._startPollingExtraction();
        } else {
          // Not started — trigger full extraction (new session, first visit)
          this._triggerExtraction();
        }
      },
      error: () => this._triggerExtraction(),
    });
  }

  private _triggerExtraction(): void {
    if (!this._sessionId) { return; }
    this.currentPhase.set('extraction');
    this.extractionProgress.set(0);
    this.extractionMessage.set('🚀 Sending your documents to the extraction engine…');
    this.extractionSvc.triggerExtraction(this._sessionId).subscribe({
      next:  () => this._startPollingExtraction(),
      error: (err: Error) => this._showError(err.message ?? 'Failed to start document extraction.'),
    });
  }

  private _startPollingExtraction(): void {
    this._stopPolling();
    this._pollExtractionStatus();
    this._pollTimer = setInterval(() => this._pollExtractionStatus(), POLL_INTERVAL_MS);
  }

  private _pollExtractionStatus(): void {
    if (!this._sessionId) { return; }
    this.extractionSvc.getExtractionStatus(this._sessionId).subscribe({
      next: (data) => {
        if (data.progress !== undefined) { this.extractionProgress.set(data.progress); }
        if (data.message)               { this.extractionMessage.set(data.message); }
        if (data.current_file)          { this.currentFile.set(data.current_file); }
        if (data.total_files > 0) {
          this.extractionDetail.set(
            `${data.completed_files} of ${data.total_files} file(s) processed`
          );
        }        if (data.status === 'failed') {
          this._stopPolling();
          this._showError('Content extraction failed. Please try again.');
          return;
        }
        if (data.extraction_done) {
          this._stopPolling();
          this.extractionProgress.set(100);
          this.extractionMessage.set('✅ All content extracted successfully!');
          this.currentFile.set('');
          this.extractionDone.set(true);
          setTimeout(() => {
            this.currentPhase.set('analysis');
            this._startCategoryAnalysis();
          }, 700);
        }
      },
      error: (err: Error) => {
        console.warn('[CategoryReview] Extraction poll error (retrying):', err.message);
      },
    });
  }

  private _startCategoryAnalysis(): void {
    if (!this._sessionId) { return; }
    this.analysisMessage.set('🤖 Kicking off the AI guardrail analysis…');
    this.categorySvc.triggerSuggestion(this._sessionId).subscribe({
      next:  () => this._startPollingAnalysis(),
      error: (err: Error) => this._showError(err.message ?? 'Failed to start AI category analysis.'),
    });
  }

  private _startPollingAnalysis(): void {
    this._stopPolling();
    this._pollAnalysisStatus();
    this._pollTimer = setInterval(() => this._pollAnalysisStatus(), POLL_INTERVAL_MS);
  }

  private _pollAnalysisStatus(): void {
    if (!this._sessionId) { return; }
    this.categorySvc.getSuggestionStatus(this._sessionId).subscribe({
      next: (data) => {
        this.analysisProgress.set(data.progress);
        this.analysisMessage.set(data.message);
        if (data.status === 'completed') {
          this._stopPolling();
          this.analysisDone.set(true);
          this._fetchResults();
        } else if (data.status === 'failed') {
          this._stopPolling();
          this._showError(data.error ?? 'AI analysis failed. Please try again.');
        }
      },
      error: (err: Error) => {
        console.warn('[CategoryReview] Analysis poll error (retrying):', err.message);
      },
    });
  }

  /**
   * Module 8 — On analysis complete, fetch both:
   *  1. AI-suggested categories (LLM results)
   *  2. All available categories (for the left panel)
   *
   * Then populate left/right panels accordingly.
   */
  private _fetchResults(): void {
    if (!this._sessionId) { return; }

    // Fire both requests in parallel for speed
    this.categorySvc.getSuggestions(this._sessionId).subscribe({
      next: (res) => {
        this.metadata.set(res.metadata);
        const aiSuggested = res.suggested_categories;

        // Fetch all available categories for the left panel
        this.categorySvc.getAvailableCategories(this._sessionId!).subscribe({
          next: (available) => {
            this.allCategoryNames.set(available.categories);

            // Build the persistent AI data map (keyed by category name)
            const map = new Map<string, SuggestedCategory>();
            aiSuggested.forEach(cat => map.set(cat.category_name, cat));
            this.aiDataMap.set(map);

            // Pre-populate right panel with AI-suggested categories (all auto-selected)
            const aiEntries: AiSelectedEntry[] = aiSuggested.map(cat => ({
              type:         'ai',
              name:         cat.category_name,
              aiData:       cat,
              dropdownOpen: false,
            }));
            this.selectedList.set(aiEntries);

            this.pageState.set('ready');
          },
          error: (err: Error) => {
            // If available categories fetch fails, still show with empty left panel
            console.warn('[CategoryReview] Failed to load available categories:', err.message);
            const aiEntries: AiSelectedEntry[] = aiSuggested.map(cat => ({
              type:         'ai',
              name:         cat.category_name,
              aiData:       cat,
              dropdownOpen: false,
            }));
            this.selectedList.set(aiEntries);
            // Build AI data map even on error
            const map = new Map<string, SuggestedCategory>();
            aiSuggested.forEach(cat => map.set(cat.category_name, cat));
            this.aiDataMap.set(map);
            // Use AI category names as a partial list
            this.allCategoryNames.set(aiSuggested.map(c => c.category_name));
            this.pageState.set('ready');
          },
        });
      },
      error: (err: Error) => this._showError(err.message ?? 'Failed to load category suggestions.'),
    });
  }

  private _stopPolling(): void {
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _showError(message: string): void {
    this.errorMessage.set(message);
    this.pageState.set('error');
  }
}
